import { strictParseInt } from '../../../shared/lib/number.no-deps'
import { I18N_replaceSlug } from '../I18N/I18N.slugs'
import { IDB_procedures } from '../IDB/IDB.dexie'
import { IDB_locationRead } from '../IDB/IDB.table.location'
import type { Post_TUiPost } from './Post.TPost'
import { Rout_toProcedureContentPage } from '../Rout/Rout.procedure'
import { Rout_toUnitContentPage } from '../Rout/Rout.unit'
import { Unit_fetchById } from '../Unit/Unit.fetch'
import {
    isLocationTarget,
    isProcedureTarget,
    isUnitTarget,
    type LocationTarget,
    type Target,
    type UnitTarget,
} from '../../../shared/domain/posts/types.no-deps'
import { Logg_createLogger } from '../Logg/Logg.Logger'

export type Post_TFlatTarget = {
    unit: number
    location: number
    workflow: number
    procedure: number
}

/**
 * Post.toFlatTarget
 *
 * Flatten the Serv.PostTarget type as it's a nuisence to type
 * Also using -1s so they are indexed by IDB
 */
export function Post_toFlatTarget(target: Target): Post_TFlatTarget {
    if (isUnitTarget(target)) {
        return {
            unit: target.unit,
            location: target.location != null ? target.location : -1,
            workflow: target.workflow != null ? target.workflow : -1,
            procedure: target.procedure != null ? target.procedure : -1,
        }
    }

    if (isLocationTarget(target)) {
        return {
            unit: -1,
            location: target.location,
            workflow: target.workflow != null ? target.workflow : -1,
            procedure: target.procedure != null ? target.procedure : -1,
        }
    }

    if (isProcedureTarget(target)) {
        return {
            unit: -1,
            location: -1,
            workflow: -1,
            procedure: target.procedure,
        }
    }
}

/**
 * Post.fromFlatTarget
 *
 * Extract the target from a flat target
 * There are 5 types of targets:
 *  - { unit }
 *  - { unit, location, workflow, procedure }
 *  - { location }
 *  - { location, workflow, procedure }
 *  - { procedure }
 */
export function Post_fromFlatTarget(flatTarget: Post_TFlatTarget): Target {
    const { unit, location, workflow, procedure } = flatTarget

    if (unit >= 0) {
        const target: Target = { unit }
        if (location >= 0) target.location = location
        if (workflow >= 0) target.workflow = workflow
        if (procedure >= 0) target.procedure = procedure
        return target
    }

    if (location >= 0) {
        const target: Target = { location }
        if (workflow >= 0) target.workflow = workflow
        if (procedure >= 0) target.procedure = procedure
        return target
    }

    return { procedure }
}

/**
 * Post.targetIsAnIntersection
 *
 * A post target will be at an intersection for content pages navigated to from
 * with a workflow. This is because whilst completing a workflow, you have the
 * surrounding context. i.e. If they are looking at a unit, it's whilst there at
 * a location, during a workflow that is part of procedure.
 *
 * This information is used to surface more relevant content to the user.
 */
export function Post_targetIsAnIntersection(target: Target) {
    return (isUnitTarget(target) && target.location != null) || (isLocationTarget(target) && target.procedure != null)
}

interface TargetQuery {
    unit: string
    location: string
    workflow: string
    procedure: string
}

/**
 * Post.targetFromQuery
 *
 * Parses a post target from a query string object provided by ReactRouter
 * Which is basically just all the values as strings
 */
export function Post_targetFromQuery(query: TargetQuery): Target {
    const unit = strictParseInt(query.unit)
    const location = strictParseInt(query.location)
    const workflow = strictParseInt(query.workflow)
    const procedure = strictParseInt(query.procedure)

    if (unit != null) {
        const target: UnitTarget = { unit }
        if (location) target.location = location
        if (workflow) target.workflow = workflow
        if (procedure) target.procedure = procedure
        return target
    }

    if (location != null) {
        const target: LocationTarget = { location }
        if (workflow) target.workflow = workflow
        if (procedure) target.procedure = procedure
        return target
    }

    return { procedure }
}

/**
 * Post.targetName
 *
 * Get a display name for the post target
 */
export async function Post_targetName(post: Post_TUiPost) {
    const logger = Logg_createLogger(`Post_targetName`)
    if (post.contentType === 'report' || isProcedureTarget(post.target)) {
        const procedure = await IDB_procedures().get(post.target.procedure)
        return I18N_replaceSlug(procedure.name)
    }
    if (isUnitTarget(post.target)) {
        const unit = await Unit_fetchById(logger, post.target.unit)
        return unit.name
    }
    if (isLocationTarget(post.target)) {
        const location = await IDB_locationRead(post.target.location)
        return I18N_replaceSlug(location.name)
    }
    return null
}

export function Post_routeToContentPage(target: Target) {
    if (isProcedureTarget(target)) {
        Rout_toProcedureContentPage(target.procedure)
    }
    if (isUnitTarget(target)) {
        Rout_toUnitContentPage(target.unit, target)
    }
}
